import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BaseDraggableWidget } from '../Card';
import {
	addAssetToContract,
	getAsset,
	getContracts,
	getEventsByAsset,
} from '../../../api';
import useAsync from '../../../hooks/useAsync';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { BaseList } from '../../Widgets/WidgetBase';
import { pluralize, classNames } from '../../helpers/functions';
import useModal from '../../../hooks/useModal';
import { handleWidgetUpdateSettings } from '../helpers';
import SettingsContext from '../../../context/SettingsContext';
import {
	CheckCircleIcon,
	ChevronRightIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { TemplateReactSelect } from '../../Attributes/Attributes_old';
import Modal from '../../Shell/Modal';
import LoadingSkeleton from '../../Shell/LoadingSkeleton';
import Button from '../../Button';

const dateKey = {
	SKIPPED: 'estimatedDate',
	ESTIMATED: 'estimatedDate',
	SCHEDULED: 'scheduledDate',
	COMPLETED: 'completedDate',
	RENEW: 'date',
};

const TITLE = 'Contracts';
const DESCRIPTION = 'Enter description here';
const CATEGORY = 'Blank Category';

const AssetActiveContracts = ({ title, isPreview, ...rest }) => {
	const [query, setQuery] = useState('');
	const { id } = useParams();
	const { setOpen: openContractModal } = useModal('contractOverview');
	const { setOpen: openAddNewContractModal } = useModal('contractAdd');
	const [isAddToExistingContractModalOpen, setAddToExistingContractModal] =
		useState(false);
	const navigate = useNavigate();

	const { value: item } = useAsync(getAsset, { defaultPayload: { id } });

	const {
		value: activeContracts,
		status: isLoadingContracts,
		error: hasError,
		execute: onRefreshContracts,
	} = useAsync(getContracts, {
		immediate: false,
	});

	const {
		value: allContracts,
		status: isLoadingAllContracts,
		execute: onRefreshAllContracts,
	} = useAsync(getContracts, {
		immediate: false,
	});

	const { status: isAddingAssetToContract, execute: onAddAssetToContract } =
		useAsync(addAssetToContract, {
			immediate: false,
		});

	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const hasSettings = settings?.user?.widgetSettings
		? settings?.user?.widgetSettings[rest?.ctx?.data?.id]?.contractStatus
		: null;

	const [selectedOption, setSelectedOption] = useState(hasSettings || 'active');

	useEffect(() => {
		onRefreshContracts(
			{
				categoryId: selectedOption,
				assetId: id,
				pageSize: null,
			},
			null,
			{
				silent: true,
			}
		);
	}, [selectedOption]);

	useEffect(() => {
		if (isAddToExistingContractModalOpen) {
			onRefreshAllContracts({ pageSize: null, order: 'name' });
		} else {
			onRefreshContracts(
				{
					categoryId: selectedOption,
					assetId: id,
					pageSize: null,
				},
				null,
				{
					silent: true,
				}
			);
		}
	}, [isAddToExistingContractModalOpen]);

	if (isPreview) {
		return (
			<BaseDraggableWidget
				title={TITLE}
				category={CATEGORY}
				description={DESCRIPTION}
				{...rest}
				isPreview={true}
			/>
		);
	}

	const isEmpty =
		activeContracts?.rows?.length === 0
			? { isEmpty: true, flexCenter: true, skipIcon: true }
			: {};
	const isError = !!hasError ? { isError: true, flexCenter: true } : {};

	return (
		<>
			<BaseDraggableWidget
				title={TITLE}
				widgetProps={{
					isLoading:
						isLoadingContracts === 'pending' ||
						isLoadingAllContracts === 'pending',
					...isError,
					...isEmpty,
					isEmptyMessage: 'No contracts found',
					additonalMenuItems: [
						{
							name: 'Add new contract',
							onClick: () => openAddNewContractModal({ assetId: id }),
						},
						{
							name: 'Add to existing contract',
							onClick: () => setAddToExistingContractModal(true),
						},
					],
					options: [
						{ label: 'Active', value: 'active' },
						{ label: 'Expired', value: 'expired' },
						{ label: 'Voided/Done', value: 'voided' },
						{ label: 'All', value: 'all' },
					],
					optionSelected: selectedOption,
					optionsOnChange: (index, value, raw) => {
						setSelectedOption(raw?.value);
						updateSettings(
							handleWidgetUpdateSettings(
								'contractStatus',
								raw?.value,
								settings,
								rest?.ctx?.data?.id
							)
						);
					},
				}}
				{...rest}
			>
				<div>
					<BaseList
						items={activeContracts?.rows?.map((contract) => {
							const isExpired = moment().isAfter(contract?.endDate);
							const isExpiringSoon = moment().isAfter(
								moment(contract?.endDate).subtract(30, 'days')
							);

							return {
								children: (
									<div
										className={classNames(
											'grid grid-cols-2 gap-x-6 flex-1 py-2 cursor-pointer px-4 rounded-xl',
											isExpired
												? 'bg-red-50'
												: isExpiringSoon
												? 'bg-orange-50'
												: ''
										)}
										onClick={() => openContractModal({ id: contract?.id })}
									>
										<div
											className={classNames(
												'flex items-center',
												isExpired
													? 'text-gray-600'
													: isExpiringSoon
													? 'text-gray-500'
													: 'text-gray-500'
											)}
										>
											{isExpired ? (
												<ExclamationTriangleIcon className="text-red-500 w-5 h-5 mr-3" />
											) : isExpiringSoon ? (
												<ExclamationTriangleIcon className="text-orange-400 w-5 h-5 mr-3" />
											) : (
												<CheckCircleIcon className="text-green-500 w-5 h-5 mr-3" />
											)}{' '}
											<div className="flex flex-col">
												<div>{contract?.name}</div>
												{contract?.category && (
													<div className="text-xxs text-gray-500">
														{contract?.category?.name}
													</div>
												)}
											</div>
										</div>
										<div
											className={classNames(
												'flex justify-between items-center text-gray-500 text-xxs'
											)}
										>
											<div>
												{moment(contract?.endDate).format('dddd, MMM Do, YYYY')}

												<div className="text-xxs font-base text-gray-400">
													{moment(contract?.endDate).fromNow()}
												</div>
											</div>
											<div>
												<ChevronRightIcon className="text-gray-500 w-3 h-3" />
											</div>
										</div>
									</div>
								),
							};
						})}
					/>
				</div>
			</BaseDraggableWidget>

			<Modal
				title="Add asset to existing contract"
				open={isAddToExistingContractModalOpen}
				onClose={() => setAddToExistingContractModal(false)}
				size="xl"
			>
				<>
					{isLoadingContracts === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<>
							<div className="grid grid-cols-1 gap-y-4 gap-x-4 text-left">
								<TemplateReactSelect
									name="Contract List"
									placeholder="Select an existing contract"
									options={allContracts?.rows.map((field) => ({
										label: field?.name,
										value: field?.id,
									}))}
									onChange={(index, contract, raw) => {
										onAddAssetToContract(
											{ id: contract?.value, assetId: id },
											() => setAddToExistingContractModal(false),
											{
												showNotification: {
													success: 'Successfully added asset to contract',
												},
											}
										);
									}}
									skip={[]}
								/>
							</div>

							<div className="col-span-2 flex justify-end mt-8">
								<Button
									type="secondary"
									onClick={() => setAddToExistingContractModal(false)}
								>
									Cancel
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>
		</>
	);
};

export default AssetActiveContracts;
